"use client"

import { ApplicationEnvironmentMolecule } from "@/app/env/store"
import { useWalletConnect } from "@/app/wallet-connect/hooks"
import {
  gachaEvmAddressSchema,
  getGachaEnvironment,
  getGachaEvmChains,
} from "@anaconda/core/gacha/evm-chains"
import { t } from "@lingui/macro"
import { toPrivyWalletProvider } from "@privy-io/cross-app-connect"
import { usePrivy, useWallets } from "@privy-io/react-auth"
import { wrap } from "@reatom/core"
import { useMolecule } from "bunshi/react"
import { useMemo, useRef } from "react"
import { type EIP1193Provider, type Hex, defineChain } from "viem"
import { z } from "zod"
import { globalWalletAppId } from "../wallet-connect/privy/privy-global-wallet-connector"
import { getGachaProfileWallet } from "./gacha-profile-wallet"
import type { GachaEvmWalletProvider } from "./portfolio/gacha-evm-transfer"

type GachaGlobalWallet = {
  address: string
  walletClientType: string
  type: "ethereum"
  chainId: string
  getEthereumProvider: () => Promise<GachaEvmWalletProvider>
  isConnected: () => Promise<boolean>
  authorizeConnection: () => Promise<void>
  switchChain: (chainId: number) => Promise<void>
}
const hex = z.custom<Hex>(
  (value) => typeof value === "string" && /^0x[0-9a-fA-F]*$/.test(value),
)
const transactionSchema = z
  .object({
    from: gachaEvmAddressSchema,
    to: gachaEvmAddressSchema,
    data: hex,
    chainId: hex.optional(),
    gas: hex.optional(),
    gasPrice: hex.optional(),
    value: hex.optional(),
  })
  .strict()

// Cross-app linked identity is only a candidate: the provider must independently
// expose this exact embedded account before any reservation or signature.
export function useGachaEvmWallets() {
  const connected = useWallets()
  const { user, authenticated } = usePrivy()
  const { collectorCryptGachaBaseUrl } = useMolecule(
    ApplicationEnvironmentMolecule,
  )
  const environment = getGachaEnvironment(collectorCryptGachaBaseUrl ?? "")
  const account = authenticated
    ? user?.linkedAccounts.find(
        (account) =>
          account.type === "cross_app" &&
          account.providerApp.id === globalWalletAppId,
      )
    : undefined
  const address =
    account?.type === "cross_app"
      ? account.embeddedWallets[0]?.address
      : undefined
  const identity = `${user?.id ?? ""}:${address ?? ""}`
  const currentIdentity = useRef(identity)
  currentIdentity.current = authenticated ? identity : ""
  const globalWallet = useMemo(() => {
    if (!environment || !address) return undefined
    const parsed = gachaEvmAddressSchema.safeParse(address)
    if (!parsed.success) return undefined
    const chains = getGachaEvmChains(environment).map((chain) =>
      defineChain({
        id: chain.chainId,
        name: chain.name,
        nativeCurrency: chain.nativeCurrency,
        rpcUrls: { default: { http: [chain.rpcUrl] } },
      }),
    )
    const first = chains[0]
    if (!first) return undefined
    let chainId = first.id
    let provider: EIP1193Provider | undefined
    const assertIdentity = () => {
      if (currentIdentity.current !== identity)
        throw new Error(
          t`Original global wallet identity is no longer connected`,
        )
    }
    const getEthereumProvider = async () => {
      assertIdentity()
      // Lazy creation does not request consent or connect on page load. Explicit
      // chainId prevents the SDK's persisted/first-chain default from retargeting.
      provider ??= toPrivyWalletProvider({
        providerAppId: globalWalletAppId,
        chains: [first, ...chains.slice(1)],
        chainId,
        smartWalletMode: false,
      })
      return provider
    }
    const verifyAccount = async (provider: EIP1193Provider) => {
      const accounts = await wrap(provider.request({ method: "eth_accounts" }))
      assertIdentity()
      if (
        !accounts.some(
          (account) => account.toLowerCase() === parsed.data.toLowerCase(),
        )
      )
        throw new Error(
          t`Original global wallet signer unavailable; reconnect the configured global wallet`,
        )
    }
    const wallet: GachaGlobalWallet = {
      address: parsed.data,
      walletClientType: "privy-global",
      type: "ethereum",
      get chainId() {
        return `eip155:${chainId}`
      },
      getEthereumProvider: async () => {
        const provider = await wrap(getEthereumProvider())
        await wrap(verifyAccount(provider))
        return {
          request: async ({ method, params }) => {
            assertIdentity()
            if (method === "eth_accounts") {
              await wrap(verifyAccount(provider))
              return [
                ...(await wrap(provider.request({ method: "eth_accounts" }))),
              ]
            }
            if (method === "eth_chainId")
              return `0x${Number(await wrap(provider.request({ method: "eth_chainId" }))).toString(16)}`
            if (method !== "eth_sendTransaction")
              throw new Error(t`Unsupported global wallet execution method`)
            const transaction = transactionSchema.parse(params?.[0])
            if (
              transaction.from.toLowerCase() !== parsed.data.toLowerCase() ||
              (transaction.chainId && Number(transaction.chainId) !== chainId)
            )
              throw new Error(
                t`Original global wallet transaction context changed`,
              )
            await wrap(verifyAccount(provider))
            if (
              Number(
                await wrap(provider.request({ method: "eth_chainId" })),
              ) !== chainId
            )
              throw new Error(t`Original global wallet chain changed`)
            return await wrap(
              provider.request({
                method: "eth_sendTransaction",
                params: [transaction],
              }),
            )
          },
        }
      },
      authorizeConnection: async () => {
        const provider = await wrap(getEthereumProvider())
        const accounts = await wrap(
          provider.request({ method: "eth_accounts" }),
        )
        assertIdentity()
        // Only this explicit user-authorization path may establish cross-app
        // consent. A different existing account is never silently replaced.
        if (accounts.length === 0)
          await wrap(provider.request({ method: "eth_requestAccounts" }))
        await wrap(verifyAccount(provider))
      },
      isConnected: async () => {
        const provider = await wrap(getEthereumProvider())
        await wrap(verifyAccount(provider))
        return true
      },
      switchChain: async (target: number) => {
        if (!chains.some((chain) => chain.id === target))
          throw new Error(t`Global wallet chain is not configured`)
        const provider = await wrap(getEthereumProvider())
        await wrap(verifyAccount(provider))
        await wrap(
          provider.request({
            method: "wallet_switchEthereumChain",
            params: [{ chainId: `0x${target.toString(16)}` }],
          }),
        )
        await wrap(verifyAccount(provider))
        if (
          Number(await wrap(provider.request({ method: "eth_chainId" }))) !==
          target
        )
          throw new Error(
            t`Global wallet switch did not select the requested chain`,
          )
        assertIdentity()
        chainId = target
      },
    }
    return wallet
  }, [address, environment, identity])
  const wallets = useMemo(() => {
    // Preserve SDK signers. Consumers match the profile address rather than
    // deriving identity from connection order or wallet-client type.
    if (
      !globalWallet ||
      connected.wallets.some(
        (wallet) =>
          wallet.address.toLowerCase() === globalWallet.address.toLowerCase(),
      )
    )
      return connected.wallets
    return [...connected.wallets, globalWallet]
  }, [connected.wallets, globalWallet])
  return { ready: connected.ready, wallets }
}

export function useGachaProfileWallet() {
  const { evmAddress, isWalletConnectorInitialized } = useWalletConnect()
  const connected = useGachaEvmWallets()
  return {
    ...connected,
    ready: connected.ready && isWalletConnectorInitialized,
    walletAddress: evmAddress,
    wallet: getGachaProfileWallet(connected.wallets, evmAddress),
  }
}
